import React, { useState, useEffect } from "react";
import { formatISTDate, getISTDateInputValue } from "../utils/dateTime";

const BASE_URL = "/v1";

function AppointmentModal({ show, onClose, pet, clinicName, clinic, onAppointmentCreated }) {

    const today = getISTDateInputValue();


    const [form, setForm] = useState({
        petId: "",
        appointmentDate: "",
        timeSlot: "",
        reason: ""
    });


    const [slots, setSlots] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState("");

    // Generate five-minute appointment slots in IST.
    const generateTimeSlots = () => {
        const arr = [];
        for (let minutes = 0; minutes <= 23 * 60 + 30; minutes += 5) {
            const hours = Math.floor(minutes / 60);
            const displayHours = hours % 12 || 12;
            const displayMinutes = String(minutes % 60).padStart(2, "0");
            const modifier = hours >= 12 ? "PM" : "AM";
            arr.push(`${displayHours}:${displayMinutes} ${modifier}`);
        }

        return arr;
    };

    const filteredSlots = slots.filter(slot => {
        if (!form.appointmentDate) return true;

        // if not today → allow all
        if (form.appointmentDate !== today) return true;

        // parse time (safe)
        const [time, modifier] = slot.split(" ");
        let [hours, minutes] = time.split(":").map(Number);
        if (modifier === "PM" && hours !== 12) hours += 12;
        if (modifier === "AM" && hours === 12) hours = 0;

        const [year, month, day] = form.appointmentDate.split("-").map(Number);
        const slotTimestamp = Date.UTC(year, month - 1, day, hours, minutes) - 330 * 60 * 1000;
        return slotTimestamp > Date.now();
    });

    // 🔹 Reset on open
    useEffect(() => {
        if (show && pet) {
            setForm({
                petId: pet.id,
                appointmentDate: "",
                timeSlot: "",
                reason: ""
            });

            setSlots(generateTimeSlots());
            setError("");
            setSuccess("");
        }
    }, [show, pet]);

    // 🔹 Fetch booked slots when date changes
    useEffect(() => {
        if (!form.appointmentDate) return;

        const fetchSlots = async () => {
            try {
                const token = localStorage.getItem("token");

                const res = await fetch(
                    `${BASE_URL}/appointments/slots?date=${form.appointmentDate}`,
                    {
                        headers: { Authorization: "Bearer " + token }
                    }
                );

                if (!res.ok) return;

                const data = await res.json();
                setBookedSlots(data);

            } catch (err) {
                console.error(err);
            }
        };

        fetchSlots();

    }, [form.appointmentDate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "appointmentDate") {
            setForm(prev => ({
                ...prev,
                appointmentDate: value,
                timeSlot: "" // 🔥 reset slot
            }));
        } else {
            setForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const createAppointment = async (form) => {
        try {
            const token = localStorage.getItem("token");

            const res = await fetch("/v1/appointments", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    petId: form.petId,
                    appointmentDate: form.appointmentDate,
                    timeSlot: form.timeSlot,
                    reason: form.reason,
                    petName: pet?.name,
                    invoiceNumber: form.invoiceNumber
                })
            });

            const data = await res.json();

            if (!res.ok) {
                return {
                    success: false,
                    message: data.message || "Failed to book appointment"
                };
            }

            return {
                success: true,
                data
            };

        } catch (err) {
            console.error(err);
            return {
                success: false,
                message: "Something went wrong"
            };
        }
    };

    const generateInvoiceNumber = () => {

        const date = getISTDateInputValue().replace(/-/g, "");

        // random 5 digit number
        const random = Math.floor(
            10000 + Math.random() * 90000
        );

        return `INV-${date}-${random}`;
    };

    // 🔥 Submit
    const handleSubmit = async () => {
        setError("");
        setSuccess("");


        if (!form.appointmentDate || !form.timeSlot) {
            setError("Date and Time Slot are required");
            return;
        }

        if (form.appointmentDate < today) {
            setError("Cannot book appointment for past date");
            return;
        }


        setLoading(true);

        // ✅ Generate only once
        const generatedInvoice = generateInvoiceNumber();

        setInvoiceNumber(generatedInvoice);

        const result = await createAppointment({
            ...form,
            invoiceNumber: generatedInvoice
        });

        if (!result.success) {
            setError(result.message);
        } else {

            setSuccess("Appointment booked successfully ✅");

            if (onAppointmentCreated) {
                onAppointmentCreated();
            }

            // ✅ Keep modal open
            // so user can print consultation bill

            setForm(prev => ({
                ...prev,
                appointmentDate: "",
                timeSlot: "",
                reason: ""
            }));
        }

        setLoading(false);
    };
    if (!show) return null;

    const handlePrintConsultation = () => {
        const todayFormatted = formatISTDate(new Date());

        const html = `
    <html>
      <head>
        <title>Consultation Invoice</title>
        <style>
          @page {
            size: A4;
            margin: 20mm;
          }

          body {
            font-family: Arial, sans-serif;
            color: #333;
          }

          .container {
            width: 100%;
          }

          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .logo {
                width: 220px;
                height: auto;
                object-fit: contain;
                }

          .clinic {
            max-width: 60%;
          }

          .right {
            text-align: right;
          }

          .line {
            border-top: 1px solid #ccc;
            margin: 15px 0;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th, td {
            padding: 10px;
          }

          th {
            border-bottom: 2px solid #000;
            text-align: left;
          }

          td {
            border-bottom: 1px solid #ddd;
          }

          .text-right {
            text-align: right;
          }

          .total {
            text-align: right;
            margin-top: 20px;
            font-size: 18px;
            font-weight: bold;
          }
        </style>
      </head>

      <body>
        <div class="container">

          <div class="header">
  <div>
    ${clinic?.logoUrl
                ? `<img src="${clinic.logoUrl}" class="logo"/>`
                : ""
            }
  </div>

  <div class="clinic">
    <h2>${clinicName || "Himalayan Pet Clinic"}</h2>
    <p>
      ${clinic?.address1 || ""}<br/>
      ${clinic?.address2 || ""}<br/>
      ${clinic?.city || ""}-  ${clinic?.state || ""} - ${clinic?.pin || ""}<br/>
      ☎ ${clinic?.phone || ""}
    </p>
        </div>

            <div class="right">
                <p><b>${invoiceNumber}</b></p>
                <p><b>DATE</b><br/>${todayFormatted}</p>
                <p><b>DUE</b><br/>On Receipt</p>
            </div>
            </div>

          <div class="line"></div>
 
          <p><b>BILL TO</b></p>

            <p style="font-size:16px; margin-bottom:6px;">
            <b>${pet?.name || "Client"}</b>
            </p>

            <div style="font-size:14px; line-height:1.6;">
            <div><b>Species:</b> ${pet?.species || "-"}</div>
            <div><b>Breed:</b> ${pet?.breed || "-"}</div>
            <div><b>Gender:</b> ${pet?.gender || "-"}</div>
            <div><b>Age:</b> ${pet?.age || "-"}</div>
            <div><b>Weight:</b> ${pet?.weight || "-"}</div>
            </div>

          <div class="line"></div>

          <table>
            <thead>
              <tr>
                <th>DESCRIPTION</th>
                <th class="text-right">RATE</th>
                <th class="text-right">QTY</th>
                <th class="text-right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Consultation</td>
                <td class="text-right">₹350.00</td>
                <td class="text-right">1</td>
                <td class="text-right">₹350.00</td>
              </tr>
            </tbody>
          </table>

          <div class="total">
            TOTAL: ₹350.00
          </div>

        </div>
      </body>
    </html>
    `;

        const win = window.open("", "_blank");
        win.document.write(html);
        win.document.close();

        win.onload = () => {
            win.print();
        };
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show fade d-block">
                <div className="modal-dialog">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5 className="modal-title">
                                Book Appointment - {pet?.name}
                            </h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <div className="modal-body">

                            {error && (
                                <div className="alert alert-danger p-2">{error}</div>
                            )}

                            {success && (
                                <div className="alert alert-success p-2">{success}</div>
                            )}

                            {/* Date */}
                            <input
                                type="date"
                                className="form-control mb-2"
                                name="appointmentDate"
                                value={form.appointmentDate}
                                onChange={handleChange}
                                min={today}
                            />

                            {/* Time Slot */}
                            <select
                                className="form-select mb-2"
                                name="timeSlot"
                                value={form.timeSlot}
                                onChange={handleChange}
                            >
                                <option value="">Select Time Slot</option>

                                {filteredSlots.map((slot, i) => (
                                    <option
                                        key={i}
                                        value={slot}
                                        disabled={bookedSlots.includes(slot)}
                                    >
                                        {slot} {bookedSlots.includes(slot) ? "(Booked)" : ""}
                                    </option>
                                ))}
                            </select>

                            {/* Reason */}
                            <textarea
                                className="form-control"
                                name="reason"
                                placeholder="Visiting Reason"
                                value={form.reason}
                                onChange={handleChange}
                            />

                        </div>

                        <div className="modal-footer">

                            <button
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Close
                            </button>

                            {success && (
                                <button
                                    className="btn btn-success"
                                    onClick={handlePrintConsultation}
                                >
                                    Print Consultation Bill
                                </button>
                            )}

                            {!success && (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? "Booking..." : "Book Appointment"}
                                </button>
                            )}

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default AppointmentModal;
